import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ArtifactTipView,
  artifactTipsApi,
  knowledgeThreadsApi,
} from '@/lib/api'

export interface UseArtifactTipAutosaveArgs {
  runToken: string
  stepId: string
  artifactIdentity: string
  /** Machine body at first save — frozen as `sourceBody` on insert only. */
  machineBody: string
  stepIndex?: number
  stepKey?: string
  /** Existing tip from list GET, when present. */
  initialTip?: ArtifactTipView | null
  enabled?: boolean
  debounceMs?: number
  /** Called after a successful write so hosts can refresh tip maps. */
  onSaved?: (tip: ArtifactTipView) => void
  /**
   * Living thread token. When set with `livingPublicId`, first/subsequent
   * writes prefer `knowledgeThreadsApi.updateArtifact` over run-keyed tip PUT.
   */
  threadToken?: string | null
  /** Living artifact `publicId` (thread store). */
  livingPublicId?: string | null
  /** Optimistic concurrency version from the living list DTO / prior write. */
  livingVersion?: number | null
}

export interface UseArtifactTipAutosaveResult {
  scheduleSave: (editedBody: string) => void
  flush: () => Promise<void>
  publicId: string | null
  version: number | null
  isSaving: boolean
  isStale: boolean
  clearStale: () => void
  lastError: unknown
}

function getResponseStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null
  }
  const ax = error as { response?: { status?: number } }
  return typeof ax.response?.status === 'number' ? ax.response.status : null
}

function tipFromLivingWrite(args: {
  publicId: string
  artifactIdentity: string
  editedBody: string
  machineBody: string
  version: number
  revision: number
  runToken: string
  stepId: string
}): ArtifactTipView {
  return {
    publicId: args.publicId,
    runToken: args.runToken,
    stepId: args.stepId,
    artifactIdentity: args.artifactIdentity,
    sourceBody: args.machineBody,
    editedBody: args.editedBody,
    version: args.version,
    revision: args.revision,
    updatedAt: new Date().toISOString(),
    derivedFrom: null,
    opsApplied: null,
    original: true,
  }
}

/**
 * Debounced tip / living-artifact autosave.
 *
 * Priority:
 * 1. Living `updateArtifact` when `threadToken` + `livingPublicId` are set
 * 2. Tip PATCH when tip publicId+version already exist (no living version path)
 * 3. Run-keyed tip PUT only as legacy first-write when living is unavailable
 *
 * On 409, refetches tip list (when runToken exists) and surfaces `isStale`
 * without overwriting the in-flight draft.
 *
 * Cache scope: N/A (client).
 */
export function useArtifactTipAutosave(
  args: UseArtifactTipAutosaveArgs,
): UseArtifactTipAutosaveResult {
  const {
    runToken,
    stepId,
    artifactIdentity,
    machineBody,
    stepIndex,
    stepKey,
    initialTip = null,
    enabled = true,
    debounceMs = 600,
    onSaved,
    threadToken = null,
    livingPublicId = null,
    livingVersion = null,
  } = args

  const [publicId, setPublicId] = useState<string | null>(
    initialTip?.publicId ?? livingPublicId ?? null,
  )
  const [version, setVersion] = useState<number | null>(
    initialTip?.version ?? livingVersion ?? null,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isStale, setIsStale] = useState(false)
  const [lastError, setLastError] = useState<unknown>(null)

  const publicIdRef = useRef(publicId)
  const versionRef = useRef(version)
  const pendingBodyRef = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef(false)
  const onSavedRef = useRef(onSaved)
  const machineBodyRef = useRef(machineBody)
  const livingVersionRef = useRef(livingVersion)

  publicIdRef.current = publicId
  versionRef.current = version
  onSavedRef.current = onSaved
  machineBodyRef.current = machineBody
  livingVersionRef.current = livingVersion

  useEffect(() => {
    if (!initialTip) {
      return
    }
    setPublicId(initialTip.publicId)
    setVersion(initialTip.version)
  }, [initialTip?.publicId, initialTip?.version])

  useEffect(() => {
    if (initialTip) {
      return
    }
    if (livingPublicId) {
      setPublicId(livingPublicId)
    }
    if (livingVersion != null) {
      setVersion(livingVersion)
    }
  }, [initialTip, livingPublicId, livingVersion])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const canPersist =
    Boolean(threadToken && livingPublicId) || Boolean(runToken)

  const persist = useCallback(
    async (editedBody: string) => {
      if (!enabled || !canPersist) {
        return
      }
      if (inFlightRef.current) {
        pendingBodyRef.current = editedBody
        return
      }

      inFlightRef.current = true
      setIsSaving(true)
      setLastError(null)

      try {
        let tip: ArtifactTipView
        const existingPublicId = publicIdRef.current
        const existingVersion = versionRef.current
        const livingId = livingPublicId
        const livingTok = threadToken

        if (livingTok && livingId) {
          if (editedBody === machineBodyRef.current && existingVersion == null) {
            return
          }
          const updated = await knowledgeThreadsApi.updateArtifact(
            livingTok,
            livingId,
            {
              body: editedBody,
              ...(existingVersion != null || livingVersionRef.current != null
                ? {
                    version:
                      existingVersion ?? livingVersionRef.current ?? undefined,
                  }
                : {}),
            },
          )
          tip = tipFromLivingWrite({
            publicId: livingId,
            artifactIdentity: livingId,
            editedBody,
            machineBody: machineBodyRef.current,
            version: updated.version,
            revision: updated.revision,
            runToken: runToken || '',
            stepId,
          })
        } else if (existingPublicId && existingVersion != null) {
          tip = await artifactTipsApi.patch(existingPublicId, {
            editedBody,
            version: existingVersion,
          })
        } else if (editedBody === machineBodyRef.current) {
          return
        } else if (runToken) {
          tip = await artifactTipsApi.upsert(runToken, {
            stepId,
            artifactIdentity,
            editedBody,
            sourceBody: machineBodyRef.current,
            stepIndex,
            stepKey,
          })
        } else {
          return
        }

        setPublicId(tip.publicId)
        setVersion(tip.version)
        setIsStale(false)
        onSavedRef.current?.(tip)
      } catch (error) {
        setLastError(error)
        if (getResponseStatus(error) === 409) {
          if (runToken) {
            try {
              const tips = await artifactTipsApi.list(runToken)
              const serverTip = tips.find(
                (row) =>
                  row.artifactIdentity === artifactIdentity ||
                  row.artifactIdentity === livingPublicId,
              )
              if (serverTip) {
                setPublicId(serverTip.publicId)
                setVersion(serverTip.version)
              }
            } catch {
              // Keep the conflict surface even if refetch fails.
            }
          }
          setIsStale(true)
        }
      } finally {
        inFlightRef.current = false
        setIsSaving(false)
        const queued = pendingBodyRef.current
        if (queued != null) {
          pendingBodyRef.current = null
          void persist(queued)
        }
      }
    },
    [
      artifactIdentity,
      canPersist,
      enabled,
      livingPublicId,
      runToken,
      stepId,
      stepIndex,
      stepKey,
      threadToken,
    ],
  )

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const body = pendingBodyRef.current
    if (body == null) {
      return
    }
    pendingBodyRef.current = null
    await persist(body)
  }, [persist])

  const scheduleSave = useCallback(
    (editedBody: string) => {
      if (!enabled) {
        return
      }
      pendingBodyRef.current = editedBody
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        const body = pendingBodyRef.current
        if (body == null) {
          return
        }
        pendingBodyRef.current = null
        void persist(body)
      }, debounceMs)
    },
    [debounceMs, enabled, persist],
  )

  function clearStale() {
    setIsStale(false)
  }

  return {
    scheduleSave,
    flush,
    publicId,
    version,
    isSaving,
    isStale,
    clearStale,
    lastError,
  }
}
